/**
 * RailSidebar — Phase 1.10 navigation.
 *
 * Two-tier layout:
 *   [ 64px rail ]  [ 240px panel ]
 *
 * The rail always shows the 5 product domains plus a Settings rail-foot.
 * The panel shows the active domain's items. A click on a rail button both
 * "focuses" that domain's panel and navigates to the domain hub URL, so the
 * user never has an ambiguous state.
 *
 * Replaces the earlier 6-group collapsing sidebar. The new layout trades
 * density for clarity: at most one domain of links is visible at a time,
 * which keeps the workspace uncluttered and aligns with the mockup approved
 * by the team on 2026-04-20.
 */

import { useMemo, useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useTranslation } from '@/core';
import { useAuth } from '@/core/auth/useAuth';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import {
  useActiveLocationModules,
  isCommercialModuleEnabled,
} from '@/entities/location';
import {
  canViewActivities,
  canManageLocationSettings,
  canManageUsersAtScope,
  canManagePOS,
  canViewPOSSales,
  canManageInventory,
  canManageSuppliers,
  canViewAccommodations,
  canViewBookings,
  canViewGuests,
  canViewPlatformInvoices,
  canViewShifts,
  canViewStaffModule,
} from '@/core/auth/modulePermissions';
import { canViewCompensation } from '@/modules/hr/utils/compensationVisibility';
import { useTreasuryPermissions } from '@/modules/treasury/hooks/useTreasuryPermissions';
import { canViewTreasury } from '@/modules/treasury/utils/permissions';
import { cn } from '@/lib/utils';
import {
  Sidebar,
  SidebarContent,
  useSidebar,
} from '@/theme/components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/theme/components/ui/collapsible';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/theme/components/ui/popover';
import {
  buildDomains,
  resolveDomainFromPath,
  type Domain,
  type DomainId,
  type GateFlags,
  type PanelItem,
} from './navConfig';

/* ------------------------------------------------------------------ */
/* Rail button                                                         */
/* ------------------------------------------------------------------ */

interface RailButtonProps {
  domain: Domain;
  active: boolean;
  /** When the rail is collapsed, the panel is hidden and clicking a
   *  domain monogram opens a floating dropdown of its items. When the
   *  rail is expanded, clicking selects the domain in the inline panel
   *  (and navigates to its hub URL). */
  collapsed: boolean;
  onSelect: (id: DomainId) => void;
  pathname: string;
  variant: RailVariant;
}

/**
 * Rail variant — drives the accent color used for the active domain
 * indicator. 'location' uses coral (Frontdesk's hero family); 'company'
 * uses ocean to match the scope band on the main edge so users
 * recognise the company scope at a glance without confusing the two.
 */
export type RailVariant = 'location' | 'company';

interface VariantTokens {
  /** 3px vertical strip on the active rail button. */
  accentBar: string;
  /** Icon color when the rail button is active. */
  accentIcon: string;
  /** Active panel-link border on the left edge. */
  panelActiveBorder: string;
  /** Icon color when a panel link is active. */
  panelActiveIcon: string;
}

const VARIANT_TOKENS: Record<RailVariant, VariantTokens> = {
  location: {
    accentBar: 'bg-coral-500',
    accentIcon: 'text-coral-600',
    panelActiveBorder: 'border-coral-500',
    panelActiveIcon: 'text-coral-600',
  },
  company: {
    accentBar: 'bg-ocean-500',
    accentIcon: 'text-ocean-700',
    panelActiveBorder: 'border-ocean-500',
    panelActiveIcon: 'text-ocean-700',
  },
};

function RailButton({ domain, active, collapsed, onSelect, pathname, variant }: RailButtonProps) {
  const Icon = domain.icon;
  const [open, setOpen] = useState(false);
  const tokens = VARIANT_TOKENS[variant];

  const button = (
    <button
      type="button"
      onClick={() => {
        if (collapsed) {
          setOpen((prev) => !prev);
        } else {
          onSelect(domain.id);
        }
      }}
      aria-label={domain.label}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative w-full flex flex-col items-center gap-1 rounded-md py-2 transition-all',
        active
          ? 'bg-sand-100 text-sand-900'
          : 'text-sand-600 hover:bg-sand-50 hover:text-sand-900',
      )}
    >
      {active && (
        <span
          aria-hidden
          className={cn('absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r', tokens.accentBar)}
        />
      )}
      <Icon className={cn('h-5 w-5', active ? tokens.accentIcon : 'text-sand-500')} />
      <span className="text-[10px] font-semibold leading-none tracking-wide uppercase">
        {domain.monogram}
      </span>
    </button>
  );

  // Expanded rail: button is enough. The inline panel handles the
  // submenu, so no floating dropdown.
  if (!collapsed) return button;

  // Collapsed rail: floating Popover with the domain items. We
  // navigate via the same onSelect → hubUrl flow when the user picks
  // an item; the items themselves close the popover via onClick.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{button}</PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={6}
        className="w-60 p-0"
      >
        <div className="border-b border-sand-200 px-3 py-2.5">
          <h3 className="font-serif text-[14px] font-semibold tracking-[-0.005em] text-sand-900">
            {domain.label}
          </h3>
        </div>
        <nav className="space-y-0.5 p-2">
          {domain.items.map((item) => (
            <PanelItemRow
              key={item.title}
              item={item}
              pathname={pathname}
              onItemClick={() => setOpen(false)}
              variant={variant}
            />
          ))}
          {domain.items.length === 0 && (
            <p className="px-3 py-3 text-xs italic text-muted-foreground">—</p>
          )}
        </nav>
      </PopoverContent>
    </Popover>
  );
}

/* ------------------------------------------------------------------ */
/* Panel items                                                         */
/* ------------------------------------------------------------------ */

function isUrlActive(pathname: string, url: string): boolean {
  return pathname === url || pathname.startsWith(`${url}/`);
}

/**
 * Item-level active check that also honours `matchPrefixes`. Lets a
 * rail entry like Treasury Operations stay highlighted on the
 * cross-page tabs (`/treasury/pending`, `/treasury/cash`) even though
 * those paths don't share its `url` prefix.
 */
function isItemActive(
  pathname: string,
  item: { url?: string; matchPrefixes?: string[]; exact?: boolean },
): boolean {
  if (item.url) {
    // `exact` items match only their own pathname (a nested sibling route
    // must not light them up); otherwise fall back to the prefix match.
    const urlMatch = item.exact ? pathname === item.url : isUrlActive(pathname, item.url);
    if (urlMatch) return true;
  }
  if (item.matchPrefixes && item.matchPrefixes.some((prefix) => isUrlActive(pathname, prefix))) return true;
  return false;
}

interface PanelLinkProps {
  url: string;
  title: string;
  icon: NonNullable<PanelItem['icon']>;
  active: boolean;
  depth?: 'root' | 'sub';
  onClick?: () => void;
  variant: RailVariant;
}

function PanelLink({ url, title, icon: Icon, active, depth = 'root', onClick, variant }: PanelLinkProps) {
  const tokens = VARIANT_TOKENS[variant];
  return (
    <NavLink
      to={url}
      onClick={onClick}
      className={cn(
        'flex items-center justify-between gap-2 pr-2 rounded-md transition-all',
        depth === 'root' ? 'pl-3 py-1.5 text-sm' : 'pl-7 py-1 text-xs',
        active
          ? cn('bg-sand-100 text-sand-900 font-medium border-l-[3px] -ml-px', tokens.panelActiveBorder)
          : 'text-sand-700 hover:bg-sand-50 hover:text-sand-900 border-l-[3px] border-transparent -ml-px',
      )}
    >
      <span className="flex items-center gap-2 min-w-0">
        <Icon className={cn(depth === 'root' ? 'h-4 w-4' : 'h-3 w-3', active ? tokens.panelActiveIcon : 'text-sand-500')} />
        <span className="truncate">{title}</span>
      </span>
    </NavLink>
  );
}

interface PanelItemRowProps {
  item: PanelItem;
  pathname: string;
  /** Optional close-on-click callback used by the rail dropdown so
   *  picking an item dismisses the floating Popover. */
  onItemClick?: () => void;
  variant: RailVariant;
}

function PanelItemRow({ item, pathname, onItemClick, variant }: PanelItemRowProps) {
  const tokens = VARIANT_TOKENS[variant];
  const isAnySubActive = (item.subItems ?? []).some((sub) => isUrlActive(pathname, sub.url));
  const [isOpen, setIsOpen] = useState(isAnySubActive);

  // Sync open state when URL changes (e.g., via command palette).
  useEffect(() => {
    if (isAnySubActive) setIsOpen(true);
  }, [isAnySubActive]);

  // Section header — non-clickable label that visually groups the items
  // beneath it. Used by Settings to split its 11 entries into 4 buckets.
  if (item.kind === 'section') {
    return (
      <div
        className="px-3 pb-1 pt-3 first:pt-1 font-mono text-[9.5px] uppercase tracking-[0.12em] text-sand-500"
        aria-hidden="true"
      >
        {item.title}
      </div>
    );
  }

  if (item.subItems && item.subItems.length > 0) {
    const Icon = item.icon;
    if (!Icon) return null;
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              'w-full flex items-center justify-between gap-2 pl-3 pr-2 py-1.5 rounded-md border-l-[3px] -ml-px transition-all text-sm',
              isAnySubActive
                ? cn('bg-sand-100 text-sand-900 font-medium', tokens.panelActiveBorder)
                : 'border-transparent text-sand-700 hover:bg-sand-50 hover:text-sand-900',
            )}
          >
            <span className="flex items-center gap-2 min-w-0">
              <Icon className={cn('h-4 w-4', isAnySubActive ? tokens.panelActiveIcon : 'text-sand-500')} />
              <span className="truncate">{item.title}</span>
            </span>
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 text-sand-400 transition-transform',
                isOpen && 'rotate-180',
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-0.5 space-y-0.5">
            {item.subItems.map((sub) => (
              <PanelLink
                key={sub.url}
                url={sub.url}
                title={sub.title}
                icon={sub.icon}
                active={isUrlActive(pathname, sub.url)}
                depth="sub"
                onClick={onItemClick}
                variant={variant}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  if (item.url && item.icon) {
    return (
      <PanelLink
        url={item.url}
        title={item.title}
        icon={item.icon}
        active={isItemActive(pathname, item)}
        depth="root"
        onClick={onItemClick}
        variant={variant}
      />
    );
  }

  return null;
}

/* ------------------------------------------------------------------ */
/* Main component                                                      */
/* ------------------------------------------------------------------ */

export interface RailSidebarProps {
  /**
   * When provided, the rail renders these domains instead of the
   * location-side `buildDomains()`. Used by `CompanyRailSidebar` to
   * inject company-scoped domains without having to re-implement the
   * rail rendering. When omitted the rail keeps its existing
   * location-scope behavior — all the gate-computation hooks below
   * run only in that branch.
   */
  domains?: Domain[];
  /** Visual variant — drives accent color tokens (coral vs ocean). */
  variant?: RailVariant;
}

export function RailSidebar({ domains: domainsOverride, variant = 'location' }: RailSidebarProps = {}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { activeLocationId } = useActiveContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile, setOpenMobile, state: sidebarState, toggleSidebar } = useSidebar();
  const isCollapsed = sidebarState === 'collapsed';

  // Location-side gate computation. We always run the hooks (React's
  // rules-of-hooks rule) but only feed the result into buildDomains
  // when no override was passed. The companion CompanyRailSidebar
  // sends its own pre-built domain list.
  const activeModules = useActiveLocationModules();
  const treasuryPermissions = useTreasuryPermissions({
    scopeType: 'location',
    locationId: activeLocationId,
  });

  const moduleEnabled = (module: Parameters<typeof isCommercialModuleEnabled>[1]) =>
    activeModules === null || isCommercialModuleEnabled(activeModules, module);

  const gates: GateFlags = useMemo(
    () => ({
      canSeeBookings: canViewBookings(user, activeLocationId),
      canSeeGuests: canViewGuests(user, activeLocationId),
      canSeeAccommodations: canViewAccommodations(user, activeLocationId),
      canSeeActivities: canViewActivities(user, activeLocationId),
      canSeePOS: canManagePOS(user, activeLocationId),
      canSeeSales: canViewPOSSales(user, activeLocationId),
      canSeeInventory: canManageInventory(user, activeLocationId),
      canSeeStaff: canViewStaffModule(user, activeLocationId),
      canSeeShifts: canViewShifts(user, activeLocationId),
      canSeeSuppliers: canManageSuppliers(user, activeLocationId),
      canSeePayroll: canViewCompensation(user, activeLocationId),
      canSeeTreasury: canViewTreasury(user, 'location', activeLocationId),
      canSeePlatformInvoices: canViewPlatformInvoices(user, activeLocationId),
      canManageTreasuryBanking: treasuryPermissions.canManageTreasuryBanking,
      canManageTreasuryReconciliation: treasuryPermissions.canManageTreasuryReconciliation,
      // Location rail → location scope: `location.manage_users` for THIS
      // site opens Users & Roles, the company codename opens it everywhere.
      canManageUsers: canManageUsersAtScope(user, activeLocationId),
      // Same seam, one level down: configuring THIS camp is
      // `location.manage_settings` here, the company codename everywhere.
      canManageLocationSettings: canManageLocationSettings(user, activeLocationId),
      modulesActivities: moduleEnabled('activities'),
      modulesPos: moduleEnabled('pos_commerce'),
      modulesInventory: moduleEnabled('inventory'),
      modulesTreasury: moduleEnabled('treasury'),
      modulesHr: moduleEnabled('hr'),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable deps captured via values
    [user, activeLocationId, activeModules, treasuryPermissions.canManageTreasuryBanking, treasuryPermissions.canManageTreasuryReconciliation],
  );

  const allDomains = useMemo(
    () => domainsOverride ?? buildDomains({ t, gates, locationId: activeLocationId }),
    [t, gates, domainsOverride, activeLocationId],
  );
  const visibleDomains = useMemo(
    () => allDomains.filter((d) => d.visible && d.id !== 'settings'),
    [allDomains],
  );
  const settingsDomain = useMemo(
    () => allDomains.find((d) => d.id === 'settings' && d.visible),
    [allDomains],
  );

  // Use the first domain as fallback so the company rail doesn't snap
  // back to 'frontdesk' (a location-only domain) when no path matches.
  const urlDomainId = resolveDomainFromPath(
    location.pathname,
    allDomains,
    allDomains[0]?.id ?? 'frontdesk',
  );

  // Allow user to preview a domain's panel without navigating away. When the
  // URL changes, we re-sync the focused domain so browser back/forward works.
  const [focusedDomainId, setFocusedDomainId] = useState<DomainId>(urlDomainId);
  useEffect(() => {
    setFocusedDomainId(urlDomainId);
  }, [urlDomainId]);

  const focusedDomain =
    allDomains.find((d) => d.id === focusedDomainId && d.visible) ??
    visibleDomains[0] ??
    settingsDomain ??
    allDomains[0];

  const handleRailSelect = (id: DomainId) => {
    setFocusedDomainId(id);
    const target = allDomains.find((d) => d.id === id);
    if (target?.hubUrl && !isUrlActive(location.pathname, target.hubUrl)) {
      navigate(target.hubUrl);
    }
    if (isMobile) setOpenMobile(false);
  };

  return (
    // The Sidebar primitive is `position: fixed inset-y-0` by default,
    // which would slide the rail behind the full-width AppHeader. We
    // push its top down by the header height (60px on lg, 56px on
    // smaller breakpoints) and shrink the height to match so the
    // domain icons stay visible underneath the header band.
    <Sidebar
      collapsible="icon"
      className="border-r border-sand-200 top-14 !h-[calc(100svh-3.5rem)] lg:top-[60px] lg:!h-[calc(100svh-60px)]"
    >
      <SidebarContent className="p-0">
        <div className="flex h-full w-full">
          {/* Rail (64px) ---------------------------------------------- */}
          <div className="flex w-16 flex-col border-r border-sand-200 bg-sand-50">
            {/* Collapse / expand toggle. Lives above the domain icons
                with a divider below so it reads as a separate cluster
                from the navigation. Same fixed height as the panel
                header (h-12) so the bottom borders line up edge-to-
                edge across the rail + panel boundary, reading as one
                continuous hairline. Hidden on mobile — the off-canvas
                drawer is opened from the AppHeader trigger and the
                drawer is always 'expanded' inside. */}
            {!isMobile && (
              <div className="flex h-12 shrink-0 items-center justify-center border-b border-sand-200 px-2">
                <button
                  type="button"
                  onClick={toggleSidebar}
                  aria-label={
                    isCollapsed
                      ? t('nav.expandPanel', 'Expand panel')
                      : t('nav.collapsePanel', 'Collapse panel')
                  }
                  aria-expanded={!isCollapsed}
                  className="flex h-8 w-full items-center justify-center rounded-md text-sand-600 transition-colors hover:bg-sand-100 hover:text-sand-900"
                >
                  {isCollapsed ? (
                    <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </div>
            )}

            <div className="flex-1 flex flex-col items-stretch gap-1 p-2 overflow-y-auto">
              {visibleDomains.map((domain) => (
                <div key={domain.id} className="flex flex-col gap-1">
                  <RailButton
                    domain={domain}
                    active={focusedDomain.id === domain.id}
                    collapsed={isCollapsed}
                    onSelect={handleRailSelect}
                    pathname={location.pathname}
                    variant={variant}
                  />
                  {domain.separated && (
                    <div className="mx-2 border-b border-sand-200" />
                  )}
                </div>
              ))}
            </div>
            {settingsDomain && (
              <div className="p-2 border-t border-sand-200">
                <RailButton
                  domain={settingsDomain}
                  active={focusedDomain.id === 'settings'}
                  collapsed={isCollapsed}
                  onSelect={handleRailSelect}
                  pathname={location.pathname}
                  variant={variant}
                />
              </div>
            )}
          </div>

          {/* Panel (240px) — animates between 240px / opacity-100 and
              0 / opacity-0 in sync with the Sidebar primitive's outer
              width transition (200ms ease-linear). When fully
              collapsed the rail buttons open a floating Popover with
              the domain's items instead. */}
          <div
            aria-hidden={isCollapsed}
            className={cn(
              'flex flex-col bg-background overflow-hidden',
              'transition-[width,opacity] duration-200 ease-linear',
              isCollapsed ? 'w-0 opacity-0 pointer-events-none' : 'w-60 opacity-100',
            )}
          >
            {/* Panel header — same fixed height (h-12) as the rail
                toggle on the left so the bottom border lines up
                edge-to-edge and reads as one continuous hairline. */}
            <div className="flex h-12 shrink-0 items-center border-b border-sand-200 px-3">
              <h2 className="font-serif text-[15px] font-semibold tracking-[-0.005em] text-sand-900 whitespace-nowrap">
                {focusedDomain.label}
              </h2>
            </div>
            <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
              {focusedDomain.items.map((item) => (
                <PanelItemRow
                  key={item.title}
                  item={item}
                  pathname={location.pathname}
                  variant={variant}
                />
              ))}
              {focusedDomain.items.length === 0 && (
                <p className="text-xs text-sand-500 px-3 py-4">
                  {t('nav.emptyDomain', 'No items available for this domain.')}
                </p>
              )}
            </nav>
          </div>
        </div>
      </SidebarContent>
    </Sidebar>
  );
}
